import { useEffect, useState } from 'react';
import { settingControllerKey } from '../../../Player/PlayerModels';
import {
  CustomOption,
  CustomOptionWrapper,
  SelectingItemMainWrapper,
  SettingPanelMultiSelectItem,
} from '../../../Player/styles';
import { PanelSettingHeader, PanelSettingLayer } from '../../../Player/styles/Panel';
import { IconCaption } from '../../../../icons';
import UsePlayerProps from '../../../../Store/usePlayerProps';
import { Box } from '../../../../styles';

const OFF_ENTRY = { language: 'off', label: 'خاموش' };

const ControlBarNativeCaptionPanel = ({ handleSettingItemClick, activeSetting }:
  {
    handleSettingItemClick: (controllerKey: settingControllerKey) => void;
    activeSetting: string | null;
  }) => {
  const video: any = document.getElementById('simple-player');
  const [isOpen, setIsOpen] = useState(false);
  const playerState = UsePlayerProps((state) => state.playerState);
  const { captionState } = playerState;
  const captions = playerState.sources?.captions || [];
  const setPlayerState = UsePlayerProps((state) => state.setPlayerState);

  const captionList = [OFF_ENTRY, ...captions.map((c) => ({ language: c.language, label: c.label }))];

  useEffect(() => {
    if (!video) return;

    const applyDefaultCaption = () => {
      if (!video.textTracks) return;
      Array.from(video.textTracks).forEach((t: any) => { t.mode = 'disabled'; });
      const current = UsePlayerProps.getState().playerState;
      const captionsData = current.sources?.captions || [];
      const defaultCaption = captionsData.find((c) => c.default && c.language !== 'off');
      if (!defaultCaption) return;
      const tracks = Array.from(video.textTracks) as TextTrack[];
      const track = tracks.find((t) => t.language === defaultCaption.srclang || t.language === defaultCaption.language);
      if (!track) return;
      track.mode = 'showing';
      setPlayerState({
        ...UsePlayerProps.getState().playerState,
        captionState: {
          prevCaptionIndex: tracks.indexOf(track),
          selectedCaptionLang: { lang: defaultCaption.language, label: defaultCaption.label },
        },
      });
    };

    // Sync Zustand state when native iOS player changes the active caption
    const syncFromNative = () => {
      if (!video.textTracks) return;
      const tracks = Array.from(video.textTracks) as TextTrack[];
      const showingTrack = tracks.find((t) => t.mode === 'showing');
      const current = UsePlayerProps.getState().playerState;
      const trackLang = showingTrack?.language || 'off';
      const captionsData = current.sources?.captions || [];
      // Map srclang back to c.language so it matches captionList keys
      const matched = captionsData.find((c) => c.srclang === trackLang || c.language === trackLang);
      const canonicalLang = trackLang === 'off' ? 'off' : (matched?.language || trackLang);
      if (current.captionState.selectedCaptionLang.lang === canonicalLang) return;
      const displayLabel = canonicalLang === 'off' ? 'خاموش' : (matched?.label || showingTrack?.label || canonicalLang);
      setPlayerState({
        ...current,
        captionState: {
          prevCaptionIndex: showingTrack ? tracks.indexOf(showingTrack) : -1,
          selectedCaptionLang: { lang: canonicalLang, label: displayLabel },
        },
      });
    };

    if (video.readyState >= 2) {
      applyDefaultCaption();
    }
    video.addEventListener('loadeddata', applyDefaultCaption);
    video.textTracks?.addEventListener('change', syncFromNative);

    return () => {
      video.removeEventListener('loadeddata', applyDefaultCaption);
      video.textTracks?.removeEventListener('change', syncFromNative);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (captionState.selectedCaptionLang.lang === 'fa' || captionState.selectedCaptionLang.lang === 'per') {
      video?.classList.add('subRtlDirection');
      video?.classList.remove('subLtrDirection');
    } else {
      video?.classList.add('subLtrDirection');
      video?.classList.remove('subRtlDirection');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captionState.selectedCaptionLang.lang]);

  const toggleMenu = (e, key) => {
    e.stopPropagation();
    handleSettingItemClick(key);
    setIsOpen(!isOpen);
  };

  const handleCaptionChange = (lang: string, label: string) => {
    if (!video?.textTracks) return;
    for (let i = 0; i < video.textTracks.length; i++) {
      video.textTracks[i].mode = 'disabled';
    }
    if (lang !== 'off') {
      // lang is c.language; track.language is srclang — look up srclang from captions data
      const captionsData = UsePlayerProps.getState().playerState.sources?.captions || [];
      const entry = captionsData.find((c) => c.language === lang);
      const srclang = entry?.srclang || lang;
      for (let i = 0; i < video.textTracks.length; i++) {
        if (video.textTracks[i].language === srclang || video.textTracks[i].language === lang) {
          video.textTracks[i].mode = 'showing';
          break;
        }
      }
    }
    // textTracks.change fires async — update state directly here too
    const current = UsePlayerProps.getState().playerState;
    setPlayerState({
      ...current,
      captionState: {
        prevCaptionIndex: lang === 'off' ? -1 : Array.from(video.textTracks).findIndex((t: any) => t.language === lang),
        selectedCaptionLang: { lang, label },
      },
    });
  };

  return (
    <>
      {!activeSetting &&
        <SettingPanelMultiSelectItem icon={<IconCaption />} label="player.caption"
          onClick={(e) => toggleMenu(e, 'caption')}
          value={captionState.selectedCaptionLang.label as string} />}
      {activeSetting === 'caption' && <PanelSettingLayer>
        <CaptionList
          returnAction={(e) => toggleMenu(e, null)}
          selectedValue={captionState.selectedCaptionLang.lang}
          list={captionList}
          onChange={(lang: string, label: string) => {
            if (lang !== captionState.selectedCaptionLang.lang) {
              handleCaptionChange(lang, label);
            }
            setIsOpen(false);
          }} />
      </PanelSettingLayer>}
    </>
  );
};

export default ControlBarNativeCaptionPanel;

const CaptionList = ({ selectedValue, list, onChange, returnAction }) => (
  <Box>
    <PanelSettingHeader label={'player.caption'} handleClick={(e) => returnAction(e)} />
    <SelectingItemMainWrapper>
      {list.map(({ language, label }) => (<CustomOptionWrapper key={language} onClick={(e) => {
        onChange(language, label);
        returnAction(e);
      }}>
        <CustomOption selected={language === selectedValue}>
          {label}
        </CustomOption>
      </CustomOptionWrapper>))}
    </SelectingItemMainWrapper>
  </Box>
);
